import { fetcher } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  ActionListItem,
  GhostIconButton,
  Icon,
  showToast,
} from "@certn/cascadia-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

type AgreementTemplate = components["schemas"]["QuickscreenAgreementTemplate"];

export function QuickscreenConsentDocumentItem({
  consent,
  caseId,
}: {
  consent: AgreementTemplate;
  caseId: string;
}) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;

    setIsDownloading(true);

    const newWindow = window.open("", "_blank");

    try {
      type DownloadAgreementTemplateResponse =
        components["schemas"]["QuickscreenAgreementTemplateDownloadResponse"] & {
          /** Present when the API returns a direct download URL without e-sign payload. */
          presigned_url?: string;
        };

      const data = await fetcher.request<DownloadAgreementTemplateResponse>(
        `/api/internal/client-portal/quickscreen-case/${caseId}/agreement-templates/${consent.id}/download/`
      );

      const url = data.esign?.url ?? data.presigned_url;
      if (!url) {
        newWindow?.close();
        showToast(
          t(
            "The consent document is being generated. Please try again shortly."
          ),
          { type: "info" }
        );
        return;
      }

      if (newWindow) {
        newWindow.opener = null;
        newWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      newWindow?.close();
      showToast(t("Failed to download document"), { type: "error" });
    } finally {
      setIsDownloading(false);
    }
  }, [caseId, consent.id, isDownloading, t]);

  return (
    <ActionListItem
      title={consent.template_name}
      alignItems="center"
      leading={<Icon name="description" />}
      trailing={
        <GhostIconButton
          icon="download"
          isLoading={isDownloading}
          onPress={handleDownload}
        />
      }
    />
  );
}
